import React, { useState, useEffect } from 'react';
import { consolidatedCertificateService, UserShareBalance, ConsolidatedCertificate, CertificateAdminSummary } from '../../lib/consolidatedCertificateService';
import { UserCertificateLayoutEditor } from './UserCertificateLayoutEditor';
import { AdminUserEditor } from './AdminUserEditor';
import { getCurrentUser } from '../../lib/supabase';
import { filterAndRank } from '../../lib/searchRelevance';

// Sort options for the user list
type SortField = 'user_id' | 'username' | 'net_shares' | 'discrepancy' | 'needs_regen' | 'last_approved';
type SortDirection = 'asc' | 'desc';

export const ConsolidatedCertificateAdmin: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState<string>('');
  const [userBalances, setUserBalances] = useState<UserShareBalance[]>([]);
  const [bookSummary, setBookSummary] = useState<CertificateAdminSummary | null>(null);
  const [bookIncomplete, setBookIncomplete] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserShareBalance | null>(null);
  const [userCertificates, setUserCertificates] = useState<ConsolidatedCertificate[]>([]);
  const [revokedCertificates, setRevokedCertificates] = useState<ConsolidatedCertificate[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showLayoutEditor, setShowLayoutEditor] = useState(false);
  const [layoutEditorUserId, setLayoutEditorUserId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [approvingCert, setApprovingCert] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Flagging and sorting state
  const [sortField, setSortField] = useState<SortField>('last_approved');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Filter state - 3-level hierarchy
  // Level 1: KYC Status
  const [kycTab, setKycTab] = useState<'completed' | 'not_completed'>('completed');
  // Level 2: Certificate Status (only for KYC completed)
  const [certFilter, setCertFilter] = useState<'all' | 'has_certs' | 'no_certs' | 'needs_regen' | 'approved'>('all');
  // Level 3: Share Status (only for KYC completed)
  const [shareFilter, setShareFilter] = useState<'all' | 'with_shares' | 'needs_shares'>('all');

  useEffect(() => {
    loadUserBalances();
    // Get current admin email for Johan-only features
    getCurrentUser().then(u => { if (u?.email) setAdminEmail(u.email); }).catch(() => {});
  }, []);

  const loadUserBalances = async () => {
    try {
      setLoading(true);
      const loaded = await consolidatedCertificateService.getAllUserShareBalances();
      setUserBalances(loaded.users);
      setBookSummary(loaded.summary);
      setBookIncomplete(loaded.incomplete);
      if (loaded.error) {
        setError(loaded.incomplete
          ? `Certificate book load is incomplete: ${loaded.error}`
          : loaded.error);
      }
    } catch (err) {
      console.error('Error loading user balances:', err);
      setError('Failed to load user balances');
    } finally {
      setLoading(false);
    }
  };

  const loadUserCertificates = async (userId: number) => {
    try {
      const active = await consolidatedCertificateService.getUserActiveCertificates(userId);
      const revoked = await consolidatedCertificateService.getUserRevokedCertificates(userId);
      setUserCertificates(active);
      setRevokedCertificates(revoked);
    } catch (err) {
      console.error('Error loading user certificates:', err);
      setError('Failed to load user certificates');
    }
  };

  const handleViewUser = async (user: UserShareBalance) => {
    setSelectedUser(user);
    setShowUserModal(true);
    await loadUserCertificates(user.user_id);
  };

  const handleGenerateCertificates = async (userId: number, address?: string) => {
    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);

      const result = await consolidatedCertificateService.generateConsolidatedCertificates(userId, address);

      if (result.success) {
        setSuccess(`Successfully generated ${result.certificates?.length || 0} certificate(s)`);
        await loadUserCertificates(userId);
        await loadUserBalances();
      } else {
        setError(result.error || 'Failed to generate certificates');
      }
    } catch (err) {
      console.error('Error generating certificates:', err);
      setError(
        err instanceof Error && err.message.includes('session has expired')
          ? err.message
          : 'Failed to generate certificates'
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleBatchGenerate = async () => {
    if (bookIncomplete) {
      setError('Certificate book load is incomplete. Do not batch-generate from this screen.');
      return;
    }
    if (!confirm('Generate consolidated certificates for ALL users with shares? This may take several minutes.')) {
      return;
    }

    try {
      setBatchGenerating(true);
      setError(null);
      setSuccess(null);

      const result = await consolidatedCertificateService.batchGenerateAllUserCertificates();

      if (result.success) {
        setSuccess(`Batch generation complete! Successful: ${result.successful}, Failed: ${result.failed}`);
        if (result.errors.length > 0) {
          console.error('Batch generation errors:', result.errors);
        }
        await loadUserBalances();
      } else {
        setError('Batch generation failed');
      }
    } catch (err) {
      console.error('Error in batch generation:', err);
      setError('Batch generation failed');
    } finally {
      setBatchGenerating(false);
    }
  };

  const handleRevokeCertificates = async (userId: number) => {
    const reason = prompt('Enter revocation reason:');
    if (!reason) return;

    try {
      setError(null);
      setSuccess(null);

      const result = await consolidatedCertificateService.revokeUserCertificates(userId, reason);

      if (result.success) {
        setSuccess(`Revoked ${result.revoked_count} certificate(s)`);
        await loadUserCertificates(userId);
      } else {
        setError(result.error || 'Failed to revoke certificates');
      }
    } catch (err) {
      console.error('Error revoking certificates:', err);
      setError('Failed to revoke certificates');
    }
  };

  const handleApproveCertificate = async (certificateId: string, userId: number) => {
    if (!confirm('Approve this certificate? An email notification will be sent to the user.')) {
      return;
    }

    try {
      setApprovingCert(certificateId);
      setError(null);
      setSuccess(null);

      const result = await consolidatedCertificateService.approveCertificate(certificateId);

      if (result.success) {
        setSuccess('Certificate approved successfully! Email notification sent to user.');
        await loadUserCertificates(userId);
      } else {
        setError(result.error || 'Failed to approve certificate');
      }
    } catch (err) {
      console.error('Error approving certificate:', err);
      setError('Failed to approve certificate');
    } finally {
      setApprovingCert(null);
    }
  };

  // ========== FILTER HIERARCHY ==========
  // Level 1: Split by KYC Status
  const usersWithKYC = userBalances.filter(user => user.has_id_number === true);
  const usersWithoutKYC = userBalances.filter(user => !user.has_id_number);

  // Level 2 & 3 counts (for KYC completed users)
  const usersWithCerts = usersWithKYC.filter(user => user.existing_certificates > 0);
  const usersWithoutCerts = usersWithKYC.filter(user => user.existing_certificates === 0);
  const usersNeedingRegen = usersWithKYC.filter(user => user.needs_regeneration);
  const usersWithApprovedCerts = usersWithKYC.filter(user => user.last_certificate_approved_at != null);
  const usersWithShares = usersWithKYC.filter(user => user.net_whole_shares > 0);
  const usersNeedingShares = usersWithKYC.filter(user => user.net_whole_shares === 0);

  const kycCompletedCount = bookSummary?.hasId ?? usersWithKYC.length;
  const kycMissingCount = bookSummary?.noId ?? usersWithoutKYC.length;
  const hasCertsCount = bookSummary?.hasCerts ?? usersWithCerts.length;
  const noCertsCount = bookSummary?.noCerts ?? usersWithoutCerts.length;
  const needsRegenCount = bookSummary?.needsRegen ?? usersNeedingRegen.length;
  const approvedCount = bookSummary?.approved ?? usersWithApprovedCerts.length;
  const withSharesCount = bookSummary?.withShares ?? usersWithShares.length;
  const noSharesCount = bookSummary?.noShares ?? usersNeedingShares.length;

  // Get current list based on KYC tab
  let currentUserList = kycTab === 'completed' ? usersWithKYC : usersWithoutKYC;

  // Apply Level 2 filter (Certificate Status) - only for KYC completed
  if (kycTab === 'completed') {
    switch (certFilter) {
      case 'has_certs':
        currentUserList = currentUserList.filter(u => u.existing_certificates > 0);
        break;
      case 'no_certs':
        currentUserList = currentUserList.filter(u => u.existing_certificates === 0);
        break;
      case 'needs_regen':
        currentUserList = currentUserList.filter(u => u.needs_regeneration);
        break;
      case 'approved':
        currentUserList = currentUserList.filter(u => u.last_certificate_approved_at != null);
        break;
      // 'all' - no additional filter
    }

    // Apply Level 3 filter (Share Status) - only for KYC completed
    switch (shareFilter) {
      case 'with_shares':
        currentUserList = currentUserList.filter(u => u.net_whole_shares > 0);
        break;
      case 'needs_shares':
        currentUserList = currentUserList.filter(u => u.net_whole_shares === 0);
        break;
      // 'all' - no additional filter
    }
  }

  // Column sort first, then relevance ranking. filterAndRank uses a stable
  // sort and preserves input order for equal-score rows, so the column-sorted
  // order acts as the natural tiebreaker when relevance scores tie. When
  // searchTerm is empty, filterAndRank short-circuits and returns the
  // column-sorted array unchanged, preserving the existing manual sort UX.
  const columnSortedUsers = [...currentUserList].sort((a, b) => {
      let aVal: number | string | boolean;
      let bVal: number | string | boolean;

      switch (sortField) {
        case 'user_id':
          aVal = a.user_id;
          bVal = b.user_id;
          break;
        case 'username':
          aVal = (a.username || '').toLowerCase();
          bVal = (b.username || '').toLowerCase();
          break;
        case 'net_shares':
          aVal = a.net_whole_shares;
          bVal = b.net_whole_shares;
          break;
        case 'discrepancy':
          aVal = Math.abs(a.share_discrepancy || 0);
          bVal = Math.abs(b.share_discrepancy || 0);
          break;
        case 'last_approved':
          // Sort by last approved date - users with approved certs first, then by date
          const aDate = a.last_certificate_approved_at ? new Date(a.last_certificate_approved_at).getTime() : 0;
          const bDate = b.last_certificate_approved_at ? new Date(b.last_certificate_approved_at).getTime() : 0;
          return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
        case 'needs_regen':
        default:
          aVal = a.needs_regeneration ? Math.abs(a.share_discrepancy || 0) + 1000000 : 0;
          bVal = b.needs_regeneration ? Math.abs(b.share_discrepancy || 0) + 1000000 : 0;
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  const filteredAndSortedUsers = filterAndRank(
    columnSortedUsers,
    searchTerm,
    (user) => ({ primary: [user.username, user.full_name, user.user_id] }),
  );

  // Counts for stats
  const flaggedCount = needsRegenCount;

  // Handler for sort toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handler to open layout editor
  const handleOpenLayoutEditor = (userId: number) => {
    setLayoutEditorUserId(userId);
    setShowLayoutEditor(true);
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-gray-300">Loading user share balances...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Consolidated Certificate Management</h2>
            <p className="text-sm text-white mt-1">
              Manage user certificates with 10,000 share maximum, accounting for transfers
            </p>
          </div>
          <button
            onClick={handleBatchGenerate}
            disabled={batchGenerating || bookIncomplete}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors font-semibold whitespace-nowrap"
          >
            {batchGenerating ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Generating...
              </span>
            ) : 'Batch Generate All'}
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-900/30 border border-red-500/50 rounded-lg p-3 text-red-300">
            ❌ {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-900/30 border border-green-500/50 rounded-lg p-3 text-green-300">
            ✅ {success}
          </div>
        )}
        {bookIncomplete && (
          <div className="mb-4 bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3 text-yellow-200">
            Certificate book load is incomplete. Chip counts may still be short. Do not batch-generate from this screen until the load finishes.
          </div>
        )}
        {bookSummary && (
          <div className="mb-4 text-sm text-gray-300">
            Book: {bookSummary.allUsers.toLocaleString()} share-holders, {bookSummary.liveWhole.toLocaleString()} live whole, {bookSummary.onCerts.toLocaleString()} on approved certs.
          </div>
        )}

        {/* Level 1: KYC Status Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => { setKycTab('completed'); setCertFilter('all'); setShareFilter('all'); }}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              kycTab === 'completed'
                ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            ✅ KYC Completed ({kycCompletedCount})
          </button>
          <button
            onClick={() => { setKycTab('not_completed'); setCertFilter('all'); setShareFilter('all'); }}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              kycTab === 'not_completed'
                ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            ❌ KYC Not Completed ({kycMissingCount})
          </button>
        </div>

        {/* Level 2 & 3 Filters - Only show for KYC Completed tab */}
        {kycTab === 'completed' && (
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {/* Level 2: Certificate Status */}
            <div className="flex items-center gap-2">
              <span className="text-white text-sm whitespace-nowrap">Certificate:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setCertFilter('all')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    certFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setCertFilter('has_certs')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    certFilter === 'has_certs'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Has Certs ({hasCertsCount})
                </button>
                <button
                  onClick={() => setCertFilter('no_certs')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    certFilter === 'no_certs'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  No Certs ({noCertsCount})
                </button>
                <button
                  onClick={() => setCertFilter('needs_regen')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    certFilter === 'needs_regen'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  🚩 Needs Regen ({needsRegenCount})
                </button>
                <button
                  onClick={() => setCertFilter('approved')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    certFilter === 'approved'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  ✅ Approved ({approvedCount})
                </button>
              </div>
            </div>

            {/* Level 3: Share Status */}
            <div className="flex items-center gap-2 sm:ml-4">
              <span className="text-white text-sm whitespace-nowrap">Shares:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setShareFilter('all')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    shareFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setShareFilter('with_shares')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    shareFilter === 'with_shares'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  With Shares ({withSharesCount})
                </button>
                <button
                  onClick={() => setShareFilter('needs_shares')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                    shareFilter === 'needs_shares'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  No Shares ({noSharesCount})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search Row */}
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder="🔍 Search by username, name, or user ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          />
          <div className="text-sm text-white flex items-center px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg">
            Showing: <span className="font-semibold text-white ml-2">{filteredAndSortedUsers.length}</span>
            <span className="ml-1">users</span>
          </div>
        </div>

        {/* Stats - Only show for KYC Completed tab */}
        {kycTab === 'completed' && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-4">
            <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-blue-400">{kycCompletedCount}</div>
              <div className="text-sm text-white">KYC Completed</div>
            </div>
            <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-green-400">{withSharesCount}</div>
              <div className="text-sm text-white">With Shares</div>
            </div>
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-purple-400">{hasCertsCount}</div>
              <div className="text-sm text-white">Has Certificates</div>
            </div>
            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-yellow-400">{noCertsCount}</div>
              <div className="text-sm text-white">No Certificates</div>
            </div>
            <div
              className={`rounded-lg p-4 cursor-pointer transition-all ${
                flaggedCount > 0
                  ? 'bg-red-900/30 border border-red-500/30 hover:bg-red-900/50'
                  : 'bg-gray-900/30 border border-gray-500/30'
              }`}
              onClick={() => flaggedCount > 0 && setCertFilter('needs_regen')}
            >
              <div className={`text-2xl sm:text-3xl font-bold ${flaggedCount > 0 ? 'text-red-400' : 'text-white'}`}>
                {flaggedCount}
              </div>
              <div className="text-sm text-white">🚩 Needs Regen</div>
            </div>
          </div>
        )}

        {/* Info for KYC Not Completed tab */}
        {kycTab === 'not_completed' && (
          <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <p className="text-red-300 text-sm">
              <strong>⚠️ Users without KYC completed.</strong> These users have not filled in their ID number in KYC verification.
              Certificates cannot be generated until they complete KYC.
            </p>
          </div>
        )}
      </div>

      {/* User List */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th
                  className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('username')}
                >
                  User {sortField === 'username' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                  Shares Breakdown
                </th>
                <th
                  className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('net_shares')}
                >
                  Net Shares {sortField === 'net_shares' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                  Certificated
                </th>
                <th
                  className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('discrepancy')}
                >
                  Discrepancy {sortField === 'discrepancy' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('needs_regen')}
                >
                  Status {sortField === 'needs_regen' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => handleSort('last_approved')}
                >
                  Last Approved {sortField === 'last_approved' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {filteredAndSortedUsers.map((user) => (
                <tr
                  key={user.user_id}
                  className={`hover:bg-gray-750 transition-colors ${user.needs_regeneration ? 'bg-red-900/20' : ''}`}
                >
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {user.needs_regeneration && <span className="text-red-500">🚩</span>}
                      <div>
                        <div className="text-sm font-medium text-white">{user.username}</div>
                        <div className="text-sm text-white">{user.full_name}</div>
                        <div className="text-sm text-gray-500">ID: {user.user_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
                    <div className="space-y-0.5">
                      <div className="text-green-400">Purchased: +{user.purchased_shares.toFixed(1)}</div>
                      <div className="text-green-400">Comm Bonus: +{(user.commission_bonus_shares || 0).toFixed(1)}</div>
                      <div className="text-green-400">🎁 Gift Bonus: +{(user.gift_bonus_shares || 0).toFixed(1)}</div>
                      <div className="text-green-400">Converted: +{(user.converted_shares || 0).toFixed(1)}</div>
                      <div className="text-white">Sent: -{user.shares_sent.toFixed(1)} | Rcvd: +{user.shares_received.toFixed(1)}</div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-lg font-bold text-yellow-400">
                      {user.net_whole_shares.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">
                      ({user.net_shares.toFixed(2)} total)
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">
                      {(user.certificated_shares || 0).toLocaleString()} shares
                    </div>
                    <div className="text-sm text-gray-500">
                      {user.existing_certificates || 0} cert(s)
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    {user.share_discrepancy !== 0 ? (
                      <div className={`text-sm font-bold ${user.share_discrepancy > 0 ? 'text-red-400' : 'text-orange-400'}`}>
                        {user.share_discrepancy > 0 ? `+${user.share_discrepancy}` : user.share_discrepancy}
                        <div className="text-sm font-normal text-white">
                          {user.share_discrepancy > 0 ? 'missing' : 'excess'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-green-400 text-sm">✓ Match</span>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    {user.needs_regeneration ? (
                      <span className="px-2 py-1 text-sm font-semibold rounded-full bg-red-900/50 text-red-300 border border-red-500/50">
                        Needs Regen
                      </span>
                    ) : user.existing_certificates > 0 ? (
                      <span className="px-2 py-1 text-sm font-semibold rounded-full bg-green-900/50 text-green-300 border border-green-500/50">
                        ✓ Valid
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-sm font-semibold rounded-full bg-gray-700 text-gray-300 border border-gray-500/50">
                        No Certs
                      </span>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    {user.last_certificate_approved_at ? (
                      <div className="text-sm">
                        <div className="text-purple-400">
                          {new Date(user.last_certificate_approved_at).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(user.last_certificate_approved_at).toLocaleTimeString()}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleViewUser(user)}
                      className={`font-medium transition-colors ${
                        user.needs_regeneration
                          ? 'text-red-400 hover:text-red-300'
                          : 'text-blue-400 hover:text-blue-300'
                      }`}
                    >
                      {user.needs_regeneration ? '🔧 Fix' : 'Manage'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedUsers.length === 0 && (
          <div className="text-center py-8 text-white">
            {searchTerm
              ? `No users found matching "${searchTerm}"`
              : kycTab === 'not_completed'
                ? 'No users without KYC found'
                : certFilter === 'needs_regen'
                  ? 'No users need certificate regeneration'
                  : certFilter === 'has_certs'
                    ? 'No users with certificates found'
                    : certFilter === 'no_certs'
                      ? 'No users without certificates found'
                      : 'No users found with current filters'}
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white">{selectedUser.username}</h3>
                <p className="text-sm text-white">{selectedUser.full_name} (ID: {selectedUser.user_id})</p>
              </div>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-white text-2xl transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Share Breakdown */}
            <div className="bg-gradient-to-r from-gray-700 to-gray-750 rounded-lg p-6 mb-6 border border-gray-600">
              <h4 className="text-lg font-bold text-white mb-4">📊 Share Calculation Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-600">
                  <span className="text-white">Direct Purchases:</span>
                  <span className="font-semibold text-green-400">+{selectedUser.purchased_shares.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-600">
                  <span className="text-white">Commission Bonus (15%):</span>
                  <span className="font-semibold text-green-400">+{(selectedUser.commission_bonus_shares || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-600">
                  <span className="text-white">🎁 Gift / Bonus Shares:</span>
                  <span className="font-semibold text-green-400">+{(selectedUser.gift_bonus_shares || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-600">
                  <span className="text-white">USDT Converted to Shares:</span>
                  <span className="font-semibold text-green-400">+{(selectedUser.converted_shares || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-600">
                  <span className="text-white">Transfers Received:</span>
                  <span className="font-semibold text-green-400">+{selectedUser.shares_received.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-600">
                  <span className="text-white">Transfers Sent:</span>
                  <span className="font-semibold text-red-400">-{selectedUser.shares_sent.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 border-gray-500 mt-2">
                  <span className="font-bold text-white">Net Shares Owned:</span>
                  <span className="font-bold text-yellow-400">{selectedUser.net_shares.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-bold text-white">Certificated Shares (Whole):</span>
                  <span className="font-bold text-yellow-400">{selectedUser.net_whole_shares.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="font-bold text-white">Certificates Needed:</span>
                  <span className="font-bold text-yellow-400">{selectedUser.certificates_needed}</span>
                </div>
              </div>
            </div>

            {/* Johan-only: Edit User Info & KYC */}
            <AdminUserEditor userId={selectedUser.user_id} adminEmail={adminEmail} onSaved={() => loadUserBalances()} />

            {/* Layout Preview/Edit Button - Always visible for admin to approve layout before generating */}
            <div className="mb-4">
              <button
                onClick={() => handleOpenLayoutEditor(selectedUser.user_id)}
                className="w-full px-4 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-500 transition-colors flex items-center justify-center gap-2"
              >
                ✏️ Preview & Edit Certificate Layout
              </button>
              <p className="text-sm text-white text-center mt-2">
                ⚠️ Review and approve the certificate layout before generating
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                onClick={() => handleGenerateCertificates(selectedUser.user_id, selectedUser.physical_address || undefined)}
                disabled={generating || selectedUser.net_whole_shares < 1}
                className="flex-1 px-4 py-3 bg-yellow-600 text-black font-semibold rounded-lg hover:bg-yellow-500 disabled:bg-gray-600 disabled:text-white disabled:cursor-not-allowed transition-colors"
              >
                {generating ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></span>
                    Generating...
                  </span>
                ) : '📄 Generate Certificates'}
              </button>
              <button
                onClick={() => handleRevokeCertificates(selectedUser.user_id)}
                disabled={userCertificates.length === 0}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-500 disabled:bg-gray-600 disabled:text-white disabled:cursor-not-allowed transition-colors"
              >
                🚫 Revoke All Certificates
              </button>
            </div>

            {/* Active Certificates */}
            <div className="mb-6">
              <h4 className="text-lg font-bold text-white mb-3">✅ Active Certificates ({userCertificates.length})</h4>
              {userCertificates.length === 0 ? (
                <div className="text-center py-8 bg-gray-700 rounded-lg text-white border border-gray-600">
                  No active certificates
                </div>
              ) : (
                <div className="space-y-3">
                  {userCertificates.map((cert) => (
                    <div key={cert.id} className="border border-gray-600 rounded-lg p-4 hover:border-yellow-500 transition-colors bg-gray-750">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="text-lg font-bold text-yellow-400">{cert.certificate_number}</span>
                            <span className="px-2 py-1 bg-green-900/50 text-green-400 text-sm font-semibold rounded border border-green-500/30">Active</span>
                            {/* Approval Status Badge */}
                            {cert.approval_status === 'approved' ? (
                              <span className="px-2 py-1 bg-emerald-900/50 text-emerald-400 text-sm font-semibold rounded border border-emerald-500/30">✓ Approved</span>
                            ) : cert.approval_status === 'pending_approval' || !cert.approval_status ? (
                              <span className="px-2 py-1 bg-yellow-900/50 text-yellow-400 text-sm font-semibold rounded border border-yellow-500/30">⏳ Pending Approval</span>
                            ) : (
                              <span className="px-2 py-1 bg-red-900/50 text-red-400 text-sm font-semibold rounded border border-red-500/30">✗ Rejected</span>
                            )}
                            {cert.is_legacy && (
                              <span className="px-2 py-1 bg-gray-700 text-white text-sm font-semibold rounded border border-gray-600">Legacy</span>
                            )}
                          </div>
                          <div className="text-sm text-white space-y-1">
                            <div>Shares: <span className="font-semibold text-white">{cert.shares_count.toLocaleString()}</span></div>
                            <div>Issued: <span className="font-semibold text-white">{new Date(cert.issue_date || cert.created_at).toLocaleDateString()}</span></div>
                            {cert.physical_address && (
                              <div>Address: <span className="font-semibold text-white">{cert.physical_address}</span></div>
                            )}
                            <div>Type: <span className="font-semibold text-white">{cert.certificate_type || 'N/A'}</span></div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          {/* Approve Button - only show if not already approved */}
                          {cert.approval_status !== 'approved' && (
                            <button
                              onClick={() => handleApproveCertificate(cert.id, selectedUser.user_id)}
                              disabled={approvingCert === cert.id}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {approvingCert === cert.id ? '⏳ Approving...' : '✓ Approve'}
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenLayoutEditor(selectedUser.user_id)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors text-sm font-medium"
                          >
                            ✏️ Edit Layout
                          </button>
                          <button
                            onClick={() => window.open(cert.certificate_data?.file_url, '_blank')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors text-sm font-medium"
                          >
                            📥 Download PDF
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Revoked Certificates */}
            <div>
              <h4 className="text-lg font-bold text-white mb-3">🚫 Revoked Certificates ({revokedCertificates.length})</h4>
              {revokedCertificates.length === 0 ? (
                <div className="text-center py-8 bg-gray-700 rounded-lg text-white border border-gray-600">
                  No revoked certificates
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {revokedCertificates.map((cert) => (
                    <div key={cert.id} className="border border-gray-600 rounded-lg p-4 bg-gray-750">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="text-sm font-bold text-white">{cert.certificate_number}</span>
                            <span className="px-2 py-1 bg-red-900/50 text-red-400 text-sm font-semibold rounded border border-red-500/30">Revoked</span>
                            {cert.is_legacy && (
                              <span className="px-2 py-1 bg-gray-700 text-gray-500 text-sm font-semibold rounded border border-gray-600">Legacy</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 space-y-1">
                            <div>Shares: <span className="text-white">{cert.shares_at_issuance?.toLocaleString() || cert.shares_count.toLocaleString()}</span></div>
                            <div>Issued: <span className="text-white">{new Date(cert.issue_date || cert.created_at).toLocaleDateString()}</span></div>
                            <div>Revoked: <span className="text-white">{cert.revoked_at ? new Date(cert.revoked_at).toLocaleDateString() : 'N/A'}</span></div>
                            {cert.revocation_reason && (
                              <div className="mt-2 text-sm text-gray-500 italic">
                                Reason: {cert.revocation_reason.length > 100
                                  ? cert.revocation_reason.substring(0, 100) + '...'
                                  : cert.revocation_reason}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(cert.certificate_data?.file_url, '_blank')}
                          className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-500 transition-colors"
                        >
                          View PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Close Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowUserModal(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Layout Editor Modal */}
      {showLayoutEditor && layoutEditorUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold text-white">✏️ Edit Certificate Layout</h3>
              <button
                onClick={() => {
                  setShowLayoutEditor(false);
                  setLayoutEditorUserId(null);
                }}
                className="text-gray-400 hover:text-white text-2xl transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <UserCertificateLayoutEditor
                userId={layoutEditorUserId}
                onLayoutSaved={() => {
                  setShowLayoutEditor(false);
                  setLayoutEditorUserId(null);
                  setSuccess('Layout saved successfully!');
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsolidatedCertificateAdmin;

